<template>
  <b-container fluid>
    <b-row class="my-4">
      <b-col lg="8" offset-lg="2">
        <template v-if="showForm">
          <ValidationObserver ref="observer">
            <h3 class="text-capitalize">{{ $t('job-create.title') }}</h3>
            <b-row class="my-4">
              <b-col lg="8">
                <h5 class="my-2">{{ $t('inputs.headings.оpportunity') }}</h5>
                <multiselect
                  v-e2e:jobCreateOpportunityMultiselect
                  v-model="opportunity"
                  label="name"
                  track-by="sfid"
                  :options="opportunityDefinitionsAsOptions"
                  :allow-empty="false"
                  :placeholder="$t('inputs.source.placeholder')"
                  :selected-label="$t('inputs.source.selected-label')"
                  :select-label="$t('inputs.source.select-label')"
                  :select-group-label="$t('inputs.source.select-group-label')"
                  :deselect-label="$t('inputs.source.deselect-label')"
                  :deselect-group-label="
                    $t('inputs.source.deselect-group-label')
                  "
                />
              </b-col>
              <b-col lg="4">
                <h5 class="my-2">{{ $t('inputs.headings.job_source') }}</h5>
                <multiselect
                  v-e2e:jobCreateJobSource
                  v-model="job_source"
                  label="text"
                  track-by="value"
                  :options="job_sources"
                  :allow-empty="false"
                  :placeholder="$t('inputs.source.placeholder')"
                  :selected-label="$t('inputs.source.selected-label')"
                  :select-label="$t('inputs.source.select-label')"
                  :select-group-label="$t('inputs.source.select-group-label')"
                  :deselect-label="$t('inputs.source.deselect-label')"
                  :deselect-group-label="
                    $t('inputs.source.deselect-group-label')
                  "
                />
              </b-col>
            </b-row>
            <template v-if="showFormDetails">
              <h5 class="my-2">{{ $t('inputs.headings.customer_details') }}</h5>
              <customer-details
                v-e2e:jobCreateCustomerDetails
                type="required"
                validation-path="customer"
                :validation-context="validation_context"
                :show-billing-fields="true"
                :job-id="jobId"
                :contact-person="contactPerson"
              />

              <b-row>
                <b-col sm>
                  <h5 class="my-2">{{ $t('inputs.headings.eid') }}</h5>
                  <simple-text
                    v-model="eid"
                    validation-path="eid"
                    :validation-context="validation_context"
                  />
                </b-col>
                <b-col sm>
                  <h5 class="my-2">
                    {{ $t('inputs.headings.invoicing_eid') }}
                  </h5>
                  <simple-text
                    v-model="invoicing_eid"
                    validation-path="invoicing_eid"
                    :validation-context="validation_context"
                  />
                </b-col>
                <b-col sm>
                  <h5 class="my-2">{{ $t('inputs.headings.project_id') }}</h5>
                  <simple-text
                    v-model="project_id"
                    validation-path="project_id"
                    :validation-context="validation_context"
                  />
                </b-col>
                <b-col sm>
                  <template v-if="context === 'admin'">
                    <h5 class="my-2">
                      {{ $t('inputs.headings.affiliate_id') }}
                    </h5>
                    <simple-text
                      v-model="affiliate_id"
                      validation-path="affiliate_id"
                      :validation-context="validation_context"
                    />
                  </template>
                </b-col>
              </b-row>

              <h4 v-if="opportunity.multiday" class="mt-2">
                {{ $t('inputs.headings.project_id') }}:
              </h4>

              <div class="mt-2" v-if="opportunity.multiday">
                <b-button
                  size="sm"
                  class="mr-2"
                  @click="() => addProjectStep(1)"
                >
                  {{ $t('job-create.project.add-step') }}
                </b-button>
              </div>

              <div
                v-for="(projectStep, index) in sortedProjectSteps"
                :key="projectStep.step"
                :class="{
                  'border-bottom': index == projectSteps.length - 1,
                  'pb-3': index == projectSteps.length - 1,
                }"
                class="border-top mt-3"
              >
                <h5 v-if="projectSteps.length > 1" class="text-muted mt-2">
                  {{
                    $t('job-create.project.step-heading', {
                      index: projectStep.order,
                    })
                  }}
                </h5>
                <template v-if="productsEnabled">
                  <h5 class="my-2">{{ $t('inputs.headings.products') }}</h5>
                  <products
                    v-e2e:products
                    :jobId="projectStep.step"
                    :hide-price-listing="['brand', 'partner'].includes(context)"
                    :hide-price-purchasing="'brand' == context"
                    :hide-price-selling="'partner' == context"
                    :context="context"
                  />
                </template>

                <h5 class="my-2">{{ $t('inputs.headings.services') }}</h5>
                <services
                  :allowDuplicateServices="allowDuplicateServices"
                  :jobId="projectStep.step"
                  :product-id="null"
                  :products="products"
                  :hide-price-listing="['brand', 'partner'].includes(context)"
                  :hide-price-purchasing="'brand' == context"
                  :hide-price-selling="'partner' == context"
                  :context="context"
                  :custom_services="customServices[projectStep.step]"
                  :shouldDisableCustomServicesCreation="projectStep.order > 1"
                />

                <div class="mt-2" v-if="opportunity.multiday">
                  <b-button
                    size="sm"
                    class="mr-2"
                    @click="() => addProjectStep(projectStep.order + 1)"
                  >
                    {{ $t('job-create.project.add-step') }}
                  </b-button>
                  <b-button
                    v-if="projectSteps.length > 1"
                    size="sm"
                    class="mr-2"
                    @click="() => removeProjectStep(projectStep.step)"
                  >
                    {{ $t('job-create.project.remove-step') }}
                  </b-button>
                </div>
              </div>

              <div class="mt-2" v-if="opportunity.multiday">
                <b-form-checkbox
                  v-model="job_sequence"
                  class="h5 d-inline-block mb-0 align-middle"
                >
                  {{ $t('job-create.project.enable-brand-ids') }}
                </b-form-checkbox>
                <h5 class="my-2">{{ $t('job-create.project-manager') }}</h5>
                <multiselect
                  v-model="projectManager"
                  label="name"
                  track-by="sfid"
                  :options="projectManagers"
                />
              </div>
              <div v-if="user && user.role == 'admin'">
                <h5 class="my-2">{{ $t('inputs.headings.brand_2') }}</h5>

                <CrudMultiSelect
                  endpoint="/data-api/brand"
                  trackBy="id"
                  :searchFields="['id', 'name']"
                  label="name"
                  v-model="brand_2"
                />
              </div>
              <template v-if="opportunity.readiness_date_required">
                <h5 class="my-2">{{ $t('inputs.headings.readiness_date') }}</h5>
                <readiness-date
                  :job-id="jobId"
                  validation-path="readiness_date"
                  :validation-context="validation_context"
                />
              </template>
              <div v-if="user && user.role == 'admin'">
                <h5 class="my-2">
                  {{ $t('inputs.headings.availability_dates') }}
                </h5>
                <appointments-list
                  :job-id="jobId"
                  validation-path="availability_dates"
                  :validation-context="validation_context"
                  :multiday="opportunity.multiday"
                />
              </div>
              <template v-if="context == 'admin'">
                <h5 class="my-2">
                  {{ $t('inputs.headings.professional_comment') }}
                </h5>
                <comments :job-id="jobId" />
              </template>
              <h5 class="my-2">
                {{ $t('inputs.headings.internal_comments') }}
              </h5>
              <rich-text-comments
                :job-id="jobId"
                :hide-actions="true"
                input-name="internal_comments"
              />
              <template
                v-if="
                  opportunity &&
                  ['Optional', 'Mandatory'].includes(
                    opportunity.planning_documentation,
                  )
                "
              >
                <h5 class="my-2">{{ $t('inputs.headings.files') }}</h5>
                <files-selector
                  :job-id="jobId"
                  input-name="planning_customer"
                  :context="context"
                />
              </template>
              <div class="my-2" v-if="customerHasBeenInformedRequired">
                <b-form-checkbox v-model="tc_accepted_by_customer" class="h5">
                  {{
                    $t(
                      'job-create.tc-accepted-by-customer.customer-is-informed',
                    )
                  }}
                </b-form-checkbox>
                <b-collapse
                  id="tc-accepted-by-customer-collapse"
                  class="mt-2"
                  :visible="true"
                >
                  <b-card>
                    <p class="lead">
                      {{
                        $t(
                          'job-create.tc-accepted-by-customer.customer-is-informed-title',
                        )
                      }}:
                    </p>
                    <p class="card-text">
                      {{
                        $t(
                          'job-create.tc-accepted-by-customer.customer-is-informed-instruction',
                        )
                      }}
                    </p>
                  </b-card>
                </b-collapse>
              </div>
            </template>
          </ValidationObserver>
        </template>
        <b-alert show :variant="changeResult.type" v-if="changeResult">
          <h3 class="alert-heading">
            {{ changeResult.title }}
            <ul v-if="changeResult.jobIds">
              <li v-for="(job_id, index) in changeResult.jobIds" :key="index">
                <router-link
                  v-e2e:createdOrderLink
                  v-if="context == 'admin'"
                  :to="`/jobs/${job_id}`"
                >
                  {{ job_id }}
                </router-link>
                <template v-else>{{ job_id }}</template>
              </li>
            </ul>
          </h3>
          <div v-if="changeResult.details">
            {{ changeResult.description }}
            <div>
              <b-collapse id="result-details" class="my-2">
                <b-card>
                  <small>
                    <pre>{{ changeResult.details }}</pre>
                  </small>
                </b-card>
              </b-collapse>
              <b-btn v-b-toggle.result-details>
                {{ $t('login.buttons.error-details') }}
              </b-btn>
            </div>
          </div>
        </b-alert>
        <template v-if="showForm && showFormDetails">
          <b-button
            v-e2e:createOrderButton
            variant="primary"
            @click="createJob"
            :disabled="loading || !canSubmitForm"
          >
            <b-spinner
              v-if="loading"
              class="gid-spinner--button mr-2"
            ></b-spinner>
            {{ $t('job-create.confirm-btn') }}
          </b-button>
        </template>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import axios from 'axios';
import Multiselect from 'vue-multiselect';
import { mapGetters } from 'vuex';

import AppointmentsList from './inputs/AppointmentsList.vue';
import Comments from './inputs/Comments.vue';
import CustomerDetails from './inputs/CustomerDetails.vue';
import FilesSelector from './inputs/FilesSelector.vue';
import SimpleText from './inputs/SimpleText.vue';
import Products from './inputs/Products.vue';
import ReadinessDate from './inputs/ReadinessDate.vue';
import RichTextComments from './inputs/RichTextComments.vue';
import Services from './inputs/Services.vue';
import CrudMultiSelect from '@gid/vue-common/components/filters/CrudMultiSelect.vue';
import { sortBy } from 'lodash';

import {
  CONTEXT_CREATE,
  CONTEXT_DESTROY,
  STORE_API_ERRORS,
} from '../store/validation.module/types';
const VALIDATION_CONTEXT = 'new-job';
import {
  OPPORTUNITIES_FETCH,
  OPPORTUNITY_OPTIONS_FETCH,
} from '../store/opportunities.module/types';
import {
  JOB_SET_EDIT,
  JOB_INIT_INPUTS,
  JOB_UPDATE_INPUT,
} from '../store/jobs.module';
import {
  CUSTOMER_DETAILS,
  SERVICES,
  READINESS_DATE,
  AVAILABILITY_DATES,
  PROFESSIONAL_COMMENT,
  INTERNAL_COMMENTS,
  PLANNING_CUSTOMER,
  JOB_SEQUENCE,
} from './inputs/_types';
import ErrorMixin from './inputs/mixins/ErrorMixin';

export default {
  mixins: [ErrorMixin],
  components: {
    AppointmentsList,
    Comments,
    CustomerDetails,
    FilesSelector,
    Multiselect,
    SimpleText,
    Products,
    ReadinessDate,
    RichTextComments,
    Services,
    CrudMultiSelect,
  },
  props: {
    context: String,
  },
  data() {
    return {
      changeResult: null,
      eid: null,
      invoicing_eid: null,
      project_id: null,
      affiliate_id: null,
      brand_2: null,
      jobId: 'project-step-1',
      job_source: null,
      job_sources: [],
      loading: false,
      opportunities: [],
      projectManager: null,
      projectManagers: [],
      tc_accepted_by_customer: null,
      validation_context: VALIDATION_CONTEXT,
      projectPrefix: 'project-step-',
      projectSteps: [],
      accessoriesTypeID: '3',
      customServices: { 'project-step-1': [] },
    };
  },
  computed: {
    ...mapGetters(['locale', 'opportunityDefinitionsAsOptions', 'user']),
    apiPathCreate() {
      switch (this.context) {
        case 'brand':
          return `/api/brand/orders/create`;
        case 'admin':
          return `/api/admin/jobs/create`;
        default:
          return null;
      }
    },
    apiPathOportunities() {
      switch (this.context) {
        case 'brand':
        case 'admin':
          return `/api/${this.context}/opportunities`;
        default:
          return null;
      }
    },
    canSubmitForm() {
      // TODO: This should take care of most of the mandatayory fields, not only TC
      if (this.context == 'admin') {
        if (this.customerHasBeenInformedRequired) {
          return this.tc_accepted_by_customer;
        }
      }
      return true;
    },
    customerHasBeenInformedRequired() {
      return (
        this.context == 'admin' &&
        this.job_source &&
        this.job_source.value == 'Phone'
      );
    },
    sortedProjectSteps() {
      return sortBy(this.projectSteps, ['order']);
    },
    opportunity: {
      get() {
        return this.$store.getters.jobOpportunity(this.jobId);
      },
      set(value) {
        [this.jobId, ...this.projectSteps.map((step) => step.step)].forEach(
          (id) => {
            this.setOpportunity(id, value);
          },
        );
      },
    },
    job_sequence: {
      get() {
        return this.$store.getters.jobInputValue(this.jobId, JOB_SEQUENCE);
      },
      set(value) {
        [this.jobId, ...this.projectSteps.map((step) => step.step)].forEach(
          (id) => {
            this.setProjectSequence(id, value);
          },
        );
      },
    },
    productsEnabled() {
      const productDefinitions = this.$store.getters.productDefinitions(
        this.sortedProjectSteps[0].step,
      );
      return Object.keys(productDefinitions).length > 0 ? true : false;
    },
    accessoriesEnabled() {
      const productDefinitions = this.$store.getters.productDefinitions(
        this.sortedProjectSteps[0].step,
      );
      return Object.values(productDefinitions).some(
        ({ productType }) => productType?.id == this.accessoriesTypeID,
      );
    },
    services() {
      let allServices = [];
      this.projectSteps.forEach((step) => {
        allServices.push(
          ...this.$store.getters
            .allSelectedServices(step.step)
            .map((service) => ({
              ...service,
              project_sequence: `${this.projectPrefix}${step.order}`,
            })),
        );
      });
      return allServices;
    },
    products() {
      let allProducts = [];
      this.projectSteps.forEach((step) => {
        allProducts.push(
          ...this.$store.getters
            .allSelectedProducts(step.step)
            .map((product) => ({
              ...product,
              project_sequence: `${this.projectPrefix}${step.order}`,
            })),
        );
      });
      return allProducts;
    },
    showForm() {
      return !this.changeResult || this.changeResult.type == 'danger';
    },
    showFormDetails() {
      return (
        this.opportunity && (this.context == 'brand' ? true : !!this.job_source)
      );
    },
    allowDuplicateServices() {
      return this.$store.getters.duplicateServicesAllowed(this.jobId);
    },
  },
  watch: {
    opportunity(newValue) {
      let apiPathProjectManagers = null;
      if (this.context == 'brand') {
        apiPathProjectManagers = `/api/brand/project-managers`;
      } else if (this.context == 'admin' && newValue) {
        apiPathProjectManagers = `/api/admin/brands/${newValue.brand_id}/project-managers`;
      }
      if (apiPathProjectManagers) {
        axios.get(apiPathProjectManagers).then((response) => {
          this.projectManagers = response.data.map((pm) => ({
            name: `${pm.first_name} ${pm.last_name}`,
            sfid: pm.sfid,
          }));
        });
      }
    },
    projectSteps(newValue) {
      newValue.forEach((step) => {
        if (!this.customServices[step.step])
          this.customServices = { ...this.customServices, [step.step]: [] };
      });
    },
  },
  created() {
    this.$store.dispatch(`validation/${CONTEXT_CREATE}`, {
      context: VALIDATION_CONTEXT,
    });
    this.fetchInitialData();
    this.clearJobState();
  },
  destroyed() {
    this.$store.dispatch(`validation/${CONTEXT_DESTROY}`, {
      context: VALIDATION_CONTEXT,
    });
  },

  methods: {
    clearJobState() {
      this.$store.commit('JOB_EDIT_CLEAR');
      this.$store.dispatch(JOB_INIT_INPUTS, {
        jobId: this.jobId,
        inputs: [
          CUSTOMER_DETAILS,
          READINESS_DATE,
          AVAILABILITY_DATES,
          PROFESSIONAL_COMMENT,
          INTERNAL_COMMENTS,
          PLANNING_CUSTOMER,
          JOB_SEQUENCE,
        ].map((name) => ({
          name,
          data: AVAILABILITY_DATES == name ? [] : null,
          edit: true,
        })),
      });
    },
    initJobStep(step) {
      this.$store.dispatch(JOB_INIT_INPUTS, {
        jobId: step,
        inputs: [SERVICES, JOB_SEQUENCE].map((name) => ({
          name,
          data: SERVICES == name ? [] : null,
          edit: true,
        })),
      });
    },
    addProjectStep(order) {
      let newStep = null;
      if (this.projectSteps.length === 0) {
        newStep = `${this.projectPrefix}1`;
      } else {
        const [lastStep] = this.projectSteps.slice(-1);
        const lastIndex = Number(
          lastStep.step.substr(this.projectPrefix.length),
        );
        newStep = `${this.projectPrefix}${lastIndex + 1}`;
      }

      if (newStep) {
        this.initJobStep(newStep);
        this.setOpportunity(newStep, this.opportunity);
        this.setProjectSequence(newStep, this.job_sequence);

        const currentOrder = this.projectSteps.map((step) => step.order);
        let newOrder =
          currentOrder.length > 0 ? Math.max(...currentOrder) + 1 : 1;

        if (order && currentOrder.includes(order)) {
          this.projectSteps = this.projectSteps.map((projectStep) => {
            if (projectStep.order >= order) {
              projectStep.order = projectStep.order + 1;
            }
            return projectStep;
          });
          newOrder = order;
        }
        this.projectSteps.push({
          step: newStep,
          order: newOrder,
        });
        return newStep;
      }
      return null;
    },
    removeProjectStep(reference) {
      const order = this.projectSteps.find(
        (step) => step.step === reference,
      ).order;
      this.projectSteps = this.projectSteps
        .filter((step) => step.step !== reference)
        .map((projectStep) => {
          if (projectStep.order > order) {
            projectStep.order = projectStep.order - 1;
          }
          return projectStep;
        });
    },
    fetchInitialData() {
      this.addProjectStep();
      this.$store.dispatch(OPPORTUNITIES_FETCH, {
        endpoint: this.apiPathOportunities,
      });
      this.$store.dispatch(OPPORTUNITY_OPTIONS_FETCH);
      axios
        .get(`/data-api/sf-picklist?api_names=Job_Source&values_only=true`)
        .then((response) => {
          this.job_sources = (
            this.context != 'admin'
              ? response.data.Job_Source.Metadata.customValue.filter(
                  (source) =>
                    !source.label.toLowerCase().startsWith('wallbox-'),
                )
              : response.data.Job_Source.Metadata.customValue
          ).map((item) => ({
            text: this.$t(`inputs.source.options.${item.label}`)
              ? this.$t(`inputs.source.options.${item.label}`)
              : item.label,
            value: item.valueName,
          }));
        });
    },
    setOpportunity(id, value) {
      if (value) {
        this.$store.dispatch(JOB_SET_EDIT, {
          init: true,
          jobId: id,
          opportunity: value,
          brandId: value.brand_id,
          context: this.context,
        });
      }
    },
    setProjectSequence(id, value) {
      this.$store.dispatch(JOB_UPDATE_INPUT, {
        jobId: id,
        inputName: JOB_SEQUENCE,
        data: value,
      });
    },
    createJob() {
      this.$refs.observer.validate().then((valid) => {
        if (valid) {
          this.sendCreateRequest();
        }
      });
    },
    sendCreateRequest() {
      this.loading = true;
      this.changeResult = null;
      const inputs = this.$store.getters.jobInputsEditedValues(this.jobId);
      const { contactPerson } = inputs[CUSTOMER_DETAILS];
      delete inputs[CUSTOMER_DETAILS].contactPerson;

      const requestData = {
        customer: inputs[CUSTOMER_DETAILS],
        readiness_date: inputs[READINESS_DATE],
        availability_dates: inputs[AVAILABILITY_DATES],
        opportunity: this.opportunity?.sfid,
        services: this.services
          .filter(({ service }) => service)
          .map((service) => {
            const { productId, ...rest } = service;
            return {
              ...rest,
              product_id: productId,
            };
          }),
        products: this.products
          .filter(({ product }) => product)
          .map((product) => ({
            product: product.product,
            quantity: product.quantity,
            supplier: product.supplier,
            supplier_sfid: product.supplier_sfid,
            serial_number: product.serial_number,
            project_sequence: product.project_sequence,
          })),
        custom_services: this.customServices[this.sortedProjectSteps[0].step],
        planning_customer: inputs[PLANNING_CUSTOMER] || undefined,
        professional_comment: inputs[PROFESSIONAL_COMMENT],
        internal_comments: inputs[INTERNAL_COMMENTS],
        tc_accepted_by_customer: this.tc_accepted_by_customer,
        source: this.job_source && this.job_source.value,
        eid: this.eid,
        invoicing_eid: this.invoicing_eid,
        project_id: this.project_id,
        affiliate_id: this.affiliate_id,
        brand_2: this.brand_2?.id,
        contact_person: { ...contactPerson },
        use_contact_person: contactPerson?.use || false,
        project_manager_sfid: this.projectManager && this.projectManager.sfid,
      };
      axios
        .put(this.apiPathCreate, requestData)
        .then((result) => {
          this.changeResult = {
            type: 'success',
            title: this.$t('job-create.title-success'),
            jobIds: Array.isArray(result.data)
              ? result.data.map((item) => item.order.job__c.api_id__c)
              : [result.data.job.api_id],
          };
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status == 422) {
              const apiErrors = this.translateErrors(
                error.response.data.errors,
              );
              this.changeResult = {
                type: 'danger',
                title:
                  this.$t('_errors.server.title') +
                  ` - ${error.response.status}`,
                description: this.$t('_errors.server.details'),
                details: apiErrors,
              };
              this.$store.dispatch(`validation/${STORE_API_ERRORS}`, {
                context: VALIDATION_CONTEXT,
                errors: apiErrors,
              });
            } else {
              this.changeResult = {
                type: 'danger',
                title:
                  this.$t('_errors.server.title') +
                  ` - ${error.response.status}`,
                description: this.$t('_errors.server.details'),
                details: error.response,
              };
            }
          } else {
            this.changeResult = {
              type: 'danger',
              title: this.$t('_errors.network.title'),
              description: this.$t('_errors.network.details'),
            };
          }
        })
        .then(() => {
          this.loading = false;
        });
    },
  },
};
</script>
